import React, { useCallback, useEffect, useState } from 'react';
import ptlocales from '@fullcalendar/core/locales/pt-br';
import enLocales from '@fullcalendar/core/locales/en-gb';
import esLocales from '@fullcalendar/core/locales/es';
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import { useToast } from '../../hooks/toast';
import { EventClickArg } from '@fullcalendar/core';
import { DateClickArg } from '@fullcalendar/interaction';
import './styles.css';
import CreateEditEvent from './CreateEdit';
import {
  IClient,
  IScheduleStatus,
  ISchedule,
  IHoliday,
  IProfessional,
} from '../../shared/interfaces';
import { CalendarCard, ProfessionalContainer } from './styles';
import {
  ASelect,
  DropdownOptions,
  PageIntro,
  PageLayout,
} from '../../components';
import { useNavigate } from 'react-router-dom';
import {
  CalendarOutlined,
  FileTextOutlined,
  PlusOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  ClockCircleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  getSchedules,
  getSelectClients,
  getSelectHolidays,
  getSelectProfessionals,
  getSelectScheduleStatus,
} from '../../http';
import { Flex, Button, Tooltip } from 'antd';

import dayjs from 'dayjs';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';
import { LanguageCode, useI18n } from '../../shared/i18n';

const calendarLocales: Record<LanguageCode, typeof ptlocales> = {
  'pt-BR': ptlocales,
  'en-US': enLocales,
  'es-ES': esLocales,
};

const Schedules: React.FC = () => {
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { permissionFind } = useAuth();
  const { t, language } = useI18n();

  const [clients, setClients] = useState<IClient[]>([]);
  const [professionals, setProfessionals] = useState<IProfessional[]>([]);
  const [professionalId, setProfessionalId] = useState<string | null>(null);
  const [schedulesStatus, setSchedulesStatus] = useState<IScheduleStatus[]>([]);
  const [modalEvent, setModalEvent] = useState<boolean>(false);
  const [start, setStart] = useState<string>('');
  const [end, setEnd] = useState<string>('');
  const [event, setEvent] = useState<ISchedule | null>(null);
  const [arg, setArg] = useState<DateClickArg | null>(null);
  const [events, setEvents] = useState<ISchedule[]>([]);
  const [holidays, setHolidays] = useState<IHoliday[]>([]);
  const [eventsHolidays, setEventsHolidays] = useState<ISchedule[]>([]);

  const listEvents = useCallback(async () => {
    try {
      const data = await getSchedules(start, end, professionalId);
      setEvents(data);
    } catch (err) {
      addToast({
        type: 'error',
        title: t('schedulesPage.error.title'),
        description: t('schedulesPage.error.listSchedules'),
      });
    }
  }, [start, end, professionalId, addToast, t]);

  const listProfessionals = useCallback(async () => {
    try {
      const data = await getSelectProfessionals();
      setProfessionals(data);
    } catch (err) {
      addToast({
        type: 'error',
        title: t('schedulesPage.error.title'),
        description: t('schedulesPage.error.listProfessionals'),
      });
    }
  }, [addToast, t]);

  const listHolidays = useCallback(async () => {
    try {
      const data = await getSelectHolidays();
      setHolidays(data);
      const days: ISchedule[] = [];
      const year = new Date().getFullYear();
      data?.forEach((holi) => {
        days.push({
          start: `${year}-${holi.month}-${holi.day}`,
          end: `${year}-${holi.month}-${holi.day}`,
          display: 'background',
          color: '#fff1f0',
          title: holi.description,
          id: `holiday-${holi.id}`,
        });
      });
      setEventsHolidays(days);
    } catch (err) {
      addToast({
        type: 'error',
        title: t('schedulesPage.error.title'),
        description: t('schedulesPage.error.listHolidays'),
      });
    }
  }, [addToast, t]);

  const existHoliday = (date: string) => {
    let finded = false;
    let description = '';
    const year = new Date().getFullYear();
    holidays.forEach((holi) => {
      const holiday = `${year}-${holi.month}-${holi.day}`;
      if (date === holiday) {
        finded = true;
        description = holi.description;
      }
    });
    return { finded, description };
  };

  const eventClick = useCallback(
    (arg: EventClickArg) => {
      if (arg.event.display === 'background') return;

      if (!permissionFind([PermissionActions.schedulesUpdate])) return;

      const eventData: ISchedule = {
        ...arg.event.extendedProps,
        title: arg.event.title,
        id: arg.event.id,
        start: arg.event.startStr,
        end: arg.event.endStr,
        color: arg.event.backgroundColor,
      };

      setArg({ dateStr: arg.event.startStr } as DateClickArg);
      setEvent(eventData);
      setModalEvent(true);
    },
    [permissionFind],
  );

  const dateClick = useCallback(
    (arg: DateClickArg) => {
      if (!permissionFind([PermissionActions.schedulesCreate])) return;

      const holiday = existHoliday(arg.dateStr);
      if (!holiday.finded) {
        setArg(arg);
        setEvent(null);
        setModalEvent(true);
      } else {
        addToast({
          type: 'info',
          title: t('schedulesPage.info.holidayTitle'),
          description: holiday.description,
        });
      }
    },
    [holidays, existHoliday, addToast, permissionFind, t],
  );

  const listClients = useCallback(async () => {
    try {
      const data = await getSelectClients(1);
      setClients(data);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: t('schedulesPage.error.title'),
        description:
          error.response?.data?.error ?? t('schedulesPage.error.listClients'),
      });
    }
  }, [addToast, t]);

  const listSchedulesStatus = useCallback(async () => {
    try {
      const data = await getSelectScheduleStatus();
      setSchedulesStatus(data);
    } catch (error: any) {
      addToast({
        type: 'error',
        title: t('schedulesPage.error.title'),
        description:
          error.response?.data?.error ?? t('schedulesPage.error.listStatus'),
      });
    }
  }, [addToast, t]);

  useEffect(() => {
    listClients();
    listProfessionals();
    listSchedulesStatus();
    listHolidays();
  }, []);

  useEffect(() => {
    if (start && end) {
      listEvents();
    }
  }, [start, end, professionalId]);

  const renderEventContent = (eventInfo: any) => {
    const color = eventInfo.event.backgroundColor || '#1890ff';
    const timeText = eventInfo.timeText || '';
    const confirmationStatus =
      eventInfo.event.extendedProps?.confirmation_status || 'pending';
    const confirmationNotes = eventInfo.event.extendedProps?.confirmation_notes;
    const confirmedAt = eventInfo.event.extendedProps?.confirmed_at;

    const StatusIcon = () => {
      if (confirmationStatus === 'confirmed')
        return <CheckCircleTwoTone twoToneColor="#52c41a" />;
      if (confirmationStatus === 'cancelled')
        return <CloseCircleTwoTone twoToneColor="#ff4d4f" />;
      return <ClockCircleOutlined style={{ color: '#faad14' }} />;
    };

    const tooltipText = confirmationNotes
      ? `${timeText} - ${eventInfo.event.title} — ${confirmationNotes}`
      : `${timeText} - ${eventInfo.event.title}`;
    const tooltipExtra = confirmedAt
      ? t('schedulesPage.info.confirmedAt', {
          date: dayjs(confirmedAt).format('DD/MM/YYYY HH:mm'),
        })
      : null;

    return (
      <Tooltip
        title={
          <span>
            {tooltipText}
            {tooltipExtra ? (
              <div style={{ marginTop: 6 }}>{tooltipExtra}</div>
            ) : null}
          </span>
        }
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '2px 6px',
            width: '100%',
            height: '100%',
            backgroundColor: color,
            borderRadius: '4px',
            overflow: 'hidden',
            borderLeft: '3px solid rgba(0,0,0,0.1)',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {timeText && (
              <span
                style={{
                  marginRight: 6,
                  fontSize: '11px',
                  fontWeight: 700,
                  color: '#fff',
                  whiteSpace: 'nowrap',
                }}
              >
                {timeText}
              </span>
            )}
            <span
              style={{
                fontSize: '12px',
                color: '#fff',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 160,
              }}
            >
              {eventInfo.event.title}
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              color: '#fff',
            }}
          >
            {confirmationNotes && (
              <Tooltip title={confirmationNotes}>
                <InfoCircleOutlined
                  style={{ color: 'rgba(255,255,255,0.9)' }}
                />
              </Tooltip>
            )}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
              }}
            >
              <StatusIcon />
              {confirmedAt ? (
                <small style={{ fontSize: 10, opacity: 0.95 }}>
                  {dayjs(confirmedAt).format('DD/MM HH:mm')}
                </small>
              ) : null}
            </div>
          </div>
        </div>
      </Tooltip>
    );
  };

  return (
    <PageLayout>
      <PageIntro
        badge={t('schedulesPage.intro.badge')}
        icon={<CalendarOutlined />}
        title={t('schedulesPage.intro.title')}
        description={t('schedulesPage.intro.description')}
        actions={
          <Flex gap={8} wrap>
            {permissionFind([PermissionActions.schedulesCreate]) ? (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setArg(null);
                  setEvent(null);
                  setModalEvent(true);
                }}
              >
                {t('schedulesPage.actions.newSchedule')}
              </Button>
            ) : null}
            <DropdownOptions
              name={t('schedulesPage.actions.reports')}
              size="middle"
              items={[
                {
                  label: t('schedulesPage.actions.schedulesReport'),
                  key: '1',
                  icon: <FileTextOutlined />,
                  onClick: () => navigate(`/schedules/print`),
                },
              ]}
            />
          </Flex>
        }
      />

      <ProfessionalContainer>
        <div className="filter-block">
          <span className="filter-label">
            {t('schedulesPage.filters.professional')}
          </span>
          <ASelect
            placeholder={t('schedulesPage.filters.professionalPlaceholder')}
            value={professionalId}
            onChange={setProfessionalId}
            style={{ width: 280 }}
            options={[
              {
                label: t('schedulesPage.filters.allProfessionals'),
                value: null,
              },
              ...professionals.map((p) => ({ label: p.name, value: p.id })),
            ]}
          />
        </div>
      </ProfessionalContainer>

      <CalendarCard
        bordered={false}
        bodyStyle={{ padding: '0 16px 16px 16px' }}
        className="shadow-sm"
      >
        <FullCalendar
          initialView="dayGridMonth"
          height={750}
          scrollTimeReset
          plugins={[
            listPlugin,
            interactionPlugin,
            timeGridPlugin,
            dayGridPlugin,
          ]}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,listWeek',
          }}
          buttonText={{
            today: t('schedulesPage.calendar.today'),
            month: t('schedulesPage.calendar.month'),
            week: t('schedulesPage.calendar.week'),
            day: t('schedulesPage.calendar.day'),
            list: t('schedulesPage.calendar.list'),
          }}
          displayEventEnd
          eventDisplay="block"
          timeZone="UTC"
          timeZoneParam="local"
          expandRows={true}
          dayMaxEvents={4}
          locale={calendarLocales[language]}
          events={events.concat(eventsHolidays)}
          eventContent={renderEventContent}
          eventClick={eventClick}
          dateClick={dateClick}
          aspectRatio={1.8}
          datesSet={(arg) => {
            const newStart = arg.startStr?.slice(0, 10);
            const newEnd = arg.endStr?.slice(0, 10);
            if (newStart !== start || newEnd !== end) {
              setStart(newStart);
              setEnd(newEnd);
            }
          }}
        />
      </CalendarCard>
      {modalEvent && (
        <CreateEditEvent
          open={modalEvent}
          close={() => setModalEvent(false)}
          clients={clients}
          professionals={professionals}
          schedulesStatus={schedulesStatus}
          event={event}
          setEvents={setEvents}
          arg={arg}
        />
      )}
    </PageLayout>
  );
};

export default Schedules;
